import React, { useEffect, useState } from 'react';
import './MultiPages.css';

import Pager from 'new-ui/Components/Pager';
import { useTranslation } from 'utils/modifiedTranslation';

import PropTypes from 'prop-types';
import Button from 'new-ui/Components/Button';
import { THEMES } from 'new-ui/constants';
import editIcon from 'assets/icons/edit_2.svg';
import deleteIcon from 'assets/icons/delete_icon.svg';
import moment from 'moment';
// eslint-disable-next-line import/no-cycle
import { CLIENT_IM_NOT_SURE } from 'new-ui/Components/Intake';
import { PROCEDURE_QUESTION_ID_BY_DISEASE, TREATMENT_QUESTION_ID_BY_DISEASE } from 'modules/questionnaire/constants';
import {
  MULTI_PAGES_QUESTIONS,
  MULTI_PAGES_QUESTIONS_TITLE,
  MULTI_PAGES_SUB_QUESTIONS,
  QUESTION_TYPE,
} from '../../constants';
import { QuestionRenderer } from '../../QuestionRenderer';
import { BulletButton } from '../SingleOptionSelect';

const MULTI_PAGES_QUESTIONS_ADD_BUTTON = {
  [MULTI_PAGES_QUESTIONS.TREATMENTS]: 'add_another_treatment',
  [MULTI_PAGES_QUESTIONS.PROCEDURES]: 'add_another_procedure',
};

const MULTI_PAGES_DID_NOT_RECEIVE = {
  [MULTI_PAGES_QUESTIONS.TREATMENTS]: 'did_not_receive_treatment',
  [MULTI_PAGES_QUESTIONS.PROCEDURES]: 'did_not_receive_procedure',
};

export const getSubquestion = (q, id) => q.subQuestions.find((a) => a.id === id);

export const getPropertyTitle = (item, id, q, t = window.t) => {
  const subQuestion = getSubquestion(q, id);
  const value = item[id];

  switch (subQuestion.type) {
    case QUESTION_TYPE.MONTH_PICKER:
      return value ? `Received last ${moment(new Date(value)).format('MM/YYYY')}` : '';
    case QUESTION_TYPE.MULTIPLE_OPTION_SEARCH_SELECT:
    case QUESTION_TYPE.MULTIPLE_OPTION_SELECT:
    case QUESTION_TYPE.SINGLE_OPTION_SELECT:
    default:
      return Array.isArray(value) ? value.map(
        (option) => t(subQuestion.options.filter((o) => o.value === option)[0]?.label),
      ).join(', ') : t(
        subQuestion.options.filter((o) => o.value === value)[0]?.label,
      );
  }
};

const MultiPages = (props) => {
  const {
    q,
    user,
    onselect,
    getCustomRenderer,
  } = props;
  const { t } = useTranslation();

  let page = 0;

  const pager = {};

  const [items, setItems] = useState(user?.condition_profile[q.id] ? [...user.condition_profile[q.id]] : []);
  const [item, setItem] = useState(null);

  const TITLE_QUESTION = MULTI_PAGES_QUESTIONS_TITLE[q.id];
  const ADD_BUTTON_TEXT = MULTI_PAGES_QUESTIONS_ADD_BUTTON[q.id];
  const DID_NOT_RECEIVE_TEXT = MULTI_PAGES_DID_NOT_RECEIVE[q.id];
  const QUESTIONS = Object.keys(MULTI_PAGES_SUB_QUESTIONS[q.id])
    .map((a) => MULTI_PAGES_SUB_QUESTIONS[q.id][a]);

  const createItem = () => {
    const nextItem = {};
    items.push(nextItem);
    nextItem.id = items.length;
    // eslint-disable-next-line no-return-assign
    QUESTIONS.forEach((question) => nextItem[question] = null);
    setItems([...items]);
    setItem(items.length - 1);
  };

  useEffect(() => {
    if (!items.length) createItem();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!items.length) createItem();
    // eslint-disable-next-line
  }, [items]);

  pager.onSetPage = (p) => { page = p; };

  const renderBullet = (itemToRender, id) => (
    itemToRender[id]
      ? <li key={id}>{ getPropertyTitle(itemToRender, id, q, t)}</li>
      : null
  );

  const renderChooser = () => (
    <div className="multi-pages-selector">
      {items.map((itemToRender, itemId) => (
        <div key={itemId} className="multi-pages-selector-treatment">
          <div className="multi-pages-selector-header">
            <div className="multi-pages-selector-title">{ getPropertyTitle(itemToRender, TITLE_QUESTION, q) }</div>
          </div>
          <div className="multi-pages-selector-body"><ul>{ QUESTIONS.filter((a) => a !== TITLE_QUESTION).map((id) => renderBullet(itemToRender, id)) }</ul></div>
          <div className="multi-pages-selector-item-menu">
            <Button icon={editIcon} title="Edit" action={() => { setItem(itemId); }} theme={THEMES.SMALL_GREY} />
            <Button
              icon={deleteIcon}
              title="Delete"
              action={
                () => {
                  const _items = [...items];
                  _items.splice(itemId, 1);
                  onselect(_items, true);
                  setItems(_items);
                }
              }
              theme={THEMES.SMALL_GREY}
            />
          </div>
        </div>
      ))}
      <div className="multi-pages-selector-menu flex">
        <Button action={createItem} title={t(`intake.${ADD_BUTTON_TEXT}`)} theme={THEMES.ORANGE} />
        <Button action={() => { onselect(items); }} title={t('general.done')} />
      </div>
    </div>
  );

  const renderEditor = () => {
    const subQuestions = q.subQuestions.filter((a) => QUESTIONS.includes(a.id));
    const pages = [];
    page = 1;
    const getQuestion = (subQuestion) => (
      <QuestionRenderer
        getCustomRenderer={getCustomRenderer}
        q={subQuestion}
        initValue={items[item][subQuestion.id]}
        showTitle
        onselect={(v) => {
          const newItem = {
            ...items[item],
            [subQuestion.id]: v,
          };
          items[item] = newItem;
          const _items = [...items];
          setItems(_items);
          if (page === subQuestions.length) {
            onselect(_items, true);
            setItem(null);
            return;
          }
          page += 1;
          pager.gotoPage(page);
          onselect(_items, true, true);
        }}
      />
    );
    subQuestions.forEach((subQuestion, idx) => {
      const question = getQuestion(subQuestion);
      pages.push(
        items.length === 1 && !idx ? (
          <div>
            {question}
            <div className="multi-pages-submenu">
              <BulletButton
                label={t(`intake.${DID_NOT_RECEIVE_TEXT}`)}
                action={() => {
                  const data = q.id === MULTI_PAGES_QUESTIONS.TREATMENTS ? TREATMENT_QUESTION_ID_BY_DISEASE : PROCEDURE_QUESTION_ID_BY_DISEASE;
                  const forQuestion = data[user.personal.condition];
                  // !!! user mutation
                  if (forQuestion) user.condition_profile[forQuestion] = 'no';
                  onselect(null, false, false, [forQuestion]);
                }}
              />
              <BulletButton
                label={t('intake.im_not_sure')}
                action={() => {
                  onselect(CLIENT_IM_NOT_SURE);
                }}
              />
            </div>
          </div>
        ) : question,
      );
    });
    return (
      <Pager
        pages={pages}
        controller={pager}
        mandatory={[1]}
        showArrows
        lastPageAction={() => {
          setItem(null);
        }}
      />
    );
  };

  const render = () => {
    if (item !== null) return renderEditor();
    return renderChooser();
  };

  return (
    <div className="multi-pages">
      {render()}
    </div>
  );
};

MultiPages.propTypes = {
  q: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onselect: PropTypes.func.isRequired,
  getCustomRenderer: PropTypes.func,
};

export default MultiPages;
